import { Component, HostListener, OnInit } from '@angular/core';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { LanguageService } from '../../services/language.service';
import { SidenavService } from '../../services/sidenav.service';
import { ScrollControlService } from '../../services/scroll-control.service';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { AuthGuard } from '../../services/auth.guard';
import { BackgroundTimerService } from '../../services/background-timer.service';
import { delay } from 'rxjs/operators';
import { InfoBarWithStatusService } from '../../services/info-bar-with-status.service';
import { UsersManagementService } from '../../services/usersManagement.service';
import { VersionService } from '../../services/version.service';
import { Permission } from '../../security/permission';
import { NotificationsService } from '../../services/notifications.service';
import { ThemeService } from '../../services/theme.service';
import { DataService } from '../../services/data.service';


@Component({
  standalone: false,
  selector: 'app-work-area-container',
  templateUrl: './work-area-container.component.html',
  styleUrls: ['./work-area-container.component.css'],
  animations: [
    trigger('sidenavWidth', [
      state('closed', style({ width: '50px', minWidth: '50px' })),
      state('open', style({ width: '15rem', minWidth: '15rem' })),
      transition('closed <=> open', animate('700ms ease-in-out')),
    ]),
  ],
})
export class WorkAreaContainerComponent implements OnInit {

  Permission = Permission; // Make enum accessible in template
  variables: any;
  title = 'Estimator';
  isCollapsed = true; // Toggle for navbar
  currentRole = '';
  currentUserName = '-';
  currentUserFirstName = '';
  currentUserLastName = '';
  currentUserEmail = '';
  /** Shown in header when /me returns tenantName or tenantId */
  currentUserTenantLabel = '';
  /** URL of the tenant logo to display in the header (null → fallback default). */
  tenantLogoUrl: string | null = null;
  currentLanguage = 'es';

  currentUserId = '';
  appVersion: string = '';
  shouldPinSidenav: boolean = false;
  isOpen: boolean = false;
  isSidenavAnimating: boolean = false;

  isScrollEnabled = true;
  isSidenavVisible = true;
  unreadCount = 0;

  get unreadCountLabel(): string {
    return this.unreadCount > 99 ? '99+' : String(this.unreadCount);
  }

  constructor(
    private scrollService: ScrollControlService,
    private languageService: LanguageService,
    public authservice: AuthService,
    private router: Router,
    private infoBar: InfoBarWithStatusService,
    private authGuard: AuthGuard,
    private backgroundTimerService: BackgroundTimerService,
    private sidenavService: SidenavService,
    private usersManagementService: UsersManagementService,
    private versionService: VersionService,
    private notificationsService: NotificationsService,
    private themeService: ThemeService,
    private dataService: DataService,
  ) { }

  ngOnInit(): void {
    this.shouldPinSidenav = this.sidenavService.shouldPinSidenavState.getValue();
    this.isOpen = this.sidenavService.sideNavState$.getValue();
    this.updateSidenavVisibility();

    this.versionService.getVersion().subscribe({
      next: (version: string) => {
        this.appVersion = version;
      },
      error: (err: any) => {
        console.error('Error loading Estimator version:', err);
        this.appVersion = '';
      }
    });

    this.languageService.language$.subscribe((lang: string) => {
      this.currentLanguage = lang;
    });
    this.languageService.translations$.subscribe((vars: any) => {
      this.variables = vars;
    });

    this.authservice.getUserData().subscribe((user) => {
      this.currentRole = user?.role ?? '';
      this.currentUserId = user?.id ?? '';
      this.currentUserFirstName = user?.name ?? '';
      this.currentUserLastName = user?.lastName ?? '';
      this.currentUserEmail = user?.email ?? '';
      const tn = user?.tenantName;
      const tid = user?.tenantId;
      this.currentUserTenantLabel = (tn && String(tn).trim()) ? String(tn).trim()
        : (tid && String(tid).trim()) ? String(tid).trim() : '';

      const fullName = [user?.name, user?.lastName]
        .filter((part) => !!part)
        .join(' ')
        .trim();

      const tokenName = this.authservice.returnUserName();
      const tokenNameValue = Array.isArray(tokenName) ? tokenName.join(' ').trim() : tokenName;

      this.currentUserName = fullName || user?.email || tokenNameValue || '-';
    });

    this.notificationsService.unreadCount$.subscribe((count: number) => {
      this.unreadCount = count || 0;
    });

    this.themeService.currentLogo$.subscribe((logo: string | null) => {
      this.tenantLogoUrl = logo;
    });

    this.scrollService.scrollEnabled$.subscribe(enabled => {
      this.isScrollEnabled = enabled;
    });

    if (!this.authservice.isAuthenticated()) {
      this.router.navigate(['/login']);

    };

    this.backgroundTimerService.runAt(
      this.authservice.returnExpireDate(),
      () => {
        this.infoBar.showSnackbar(this.variables.sessionExpiredIn15, ['info-snackbar'], 3500);
      },
      () => {
        this.infoBar.showSnackbar(this.variables.sessionExpiredInOne, ['info-snackbar'], 3500);
      },
      async () => {
        this.infoBar.showSnackbar(this.variables.sessionExpired, ['info-snackbar'], 3500);

        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 4000);
      }
    );
  }

  logout() {
    this.authGuard.logOut();
  }

  openNotifications(): void {
    this.router.navigate(['/notifications']);
  }

  get currentLanguageLabel(): string {
    if (this.currentLanguage === 'pt') {
      return 'Português';
    }

    if (this.currentLanguage === 'en') {
      return 'English';
    }

    return 'Español';
  }

  get currentLanguageFlag(): string {
    if (this.currentLanguage === 'pt') {
      return 'assets/flags/pt.svg';
    }

    if (this.currentLanguage === 'en') {
      return 'assets/flags/gb.svg';
    }

    return 'assets/flags/es.svg';
  }

  get translatedRole(): string {
    if (!this.currentRole) {
      return '-';
    }

    const roleLabels: { [key: string]: string | undefined } = {
      'SUPER-ADMIN': this.variables?.superAdmin,
      'ADMIN': this.variables?.admin,
      'USER': this.variables?.normalUser,
      'ARCHITECT': this.variables?.architect,
      'ESTIMATION-OFFICE': this.variables?.estimationOffice
    };

    return roleLabels[this.currentRole] || this.currentRole;
  }

  navigateToProjects(): void {
    this.dataService.clearProjectsFilter();
    this.router.navigate(['/projectos']);
  }

  changeLanguage(language: string): void {
    this.languageService.language = language;

    let user = {
      language: language || 'es'
    };
    if (this.currentUserId) {
      this.usersManagementService.updateUserLanguage(this.currentUserId, user).subscribe({
        next: () => this.authservice.refreshUserData(),
        error: (err) => console.error('Failed to save language preference:', err)
      });
    }
  }
  onSidenavMouseEnter() {
    if (this.shouldPinSidenav) return;
    this.isOpen = true;
    this.sidenavService.sideNavState$.next(this.isOpen);
  }

  onSidenavMouseLeave() {
    if (this.shouldPinSidenav) return;
    this.isOpen = false;
    this.sidenavService.sideNavState$.next(this.isOpen);
  }

  onCheckboxChange(event: any) {
    this.shouldPinSidenav = event;
    if (!this.shouldPinSidenav) {
      this.isOpen = false;
      this.sidenavService.sideNavState$.next(this.isOpen);
    }
    this.sidenavService.shouldPinSidenavState.next(this.shouldPinSidenav);
  }

  onSidenavAnimationStart() {
    this.isSidenavAnimating = true;
  }

  onSidenavAnimationDone() {
    this.isSidenavAnimating = false;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateSidenavVisibility();
  }

  private updateSidenavVisibility(): void {
    if (typeof window === 'undefined') {
      this.isSidenavVisible = true;
      return;
    }

    this.isSidenavVisible = window.innerWidth > 1200;

    if (!this.isSidenavVisible) {
      this.isOpen = false;
      this.shouldPinSidenav = false;
      this.sidenavService.sideNavState$.next(false);
      this.sidenavService.shouldPinSidenavState.next(false);
    }
  }

}
